import React, { useState, useMemo, useEffect } from 'react';
import { Copy, Mail, Check, Code2, Eye, FileText, Send, Zap, ZapOff, Paperclip, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { buildEmail, emailTabs } from './emailTemplateBuilder';
import { availableDocs, buildAttachments, DEFAULT_ATTACHMENTS_FOR_TEMPLATE } from '../../utils/paperworkPdfs';
import EmailComposer from './EmailComposer';
import { authedFetch } from '../../utils/staffAuth';

/**
 * Rich HTML email templates for agents.
 * - Live preview of the designed email in an iframe.
 * - Copy as HTML (rich, keeps design & images when pasted into Outlook / Gmail).
 * - Copy as plain text.
 * - Open in native email client (mailto:).
 */

const CopyBtn = ({ testId, onClick, label, tone = 'outline', busy, ok }) => (
  <Button
    data-testid={testId}
    size="sm"
    variant={tone === 'outline' ? 'outline' : undefined}
    onClick={onClick}
    className={`h-8 rounded-full text-xs gap-1 ${tone === 'primary' ? 'bg-[#FF5C39] hover:bg-[#e0492b] text-white' : ''}`}
  >
    {ok ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> {label}</>}
  </Button>
);

const copyRichHtml = async (html, plainText) => {
  // Write both HTML and plain text so Outlook/Gmail keep the design when pasted.
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    const blob = new Blob([html], { type: 'text/html' });
    const textBlob = new Blob([plainText || ''], { type: 'text/plain' });
    await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob, 'text/plain': textBlob })]);
    return;
  }
  await navigator.clipboard.writeText(html);
};

const EmailTemplates = ({ policy }) => {
  const [active, setActive] = useState('welcome');
  const [mode, setMode] = useState('preview'); // preview | html | text
  const [copied, setCopied] = useState('');
  const [resendEnabled, setResendEnabled] = useState(false);
  const [sending, setSending] = useState(false);
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const isCancelled = policy?.status === 'Cancelled' || !!policy?.cancellation;
  const availDocs = useMemo(() => availableDocs(isCancelled), [isCancelled]);
  const [attachSelected, setAttachSelected] = useState(() => new Set(DEFAULT_ATTACHMENTS_FOR_TEMPLATE.welcome || []));
  const [composerOpen, setComposerOpen] = useState(false);
  const { toast } = useToast();

  const tpl = useMemo(() => buildEmail(active, policy), [active, policy]);
  // Editable overrides — start empty; when the user types we apply them over the template.
  // Reset every time the template tab changes so the user isn't sending the wrong version.
  const [subjectOverride, setSubjectOverride] = useState('');
  const [htmlOverride, setHtmlOverride] = useState('');
  const [textOverride, setTextOverride] = useState('');
  useEffect(() => { setSubjectOverride(''); setHtmlOverride(''); setTextOverride(''); }, [active]);
  const finalSubject = subjectOverride || tpl.subject;
  const finalHtml = htmlOverride || tpl.html;
  const finalText = textOverride || tpl.text;
  const mailto = `mailto:${encodeURIComponent(policy.email || '')}?subject=${encodeURIComponent(finalSubject)}&body=${encodeURIComponent(finalText)}`;

  // Re-seed sensible attachment defaults every time the agent switches template.
  useEffect(() => {
    const defs = DEFAULT_ATTACHMENTS_FOR_TEMPLATE[active] || [];
    setAttachSelected(new Set(defs.filter((k) => availDocs.some((d) => d.key === k))));
  }, [active, availDocs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/emails/status`);
        const j = await res.json();
        if (!cancelled) setResendEnabled(!!j.resend_enabled);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const flash = (key) => { setCopied(key); setTimeout(() => setCopied(''), 1400); };

  const parseAddrs = (s) => (s || '').split(/[,;\n]/).map((x) => x.trim().toLowerCase()).filter(Boolean);
  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const toggleAttach = (key) => setAttachSelected((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const sendNow = async () => {
    if (!policy.email) {
      toast({ title: 'No customer email on file', description: 'Add an email to the customer record first.' });
      return;
    }
    const ccList = parseAddrs(cc);
    const bccList = parseAddrs(bcc);
    const badCc = ccList.find((e) => !isValidEmail(e));
    if (badCc) return toast({ title: `CC "${badCc}" is not a valid email` });
    const badBcc = bccList.find((e) => !isValidEmail(e));
    if (badBcc) return toast({ title: `BCC "${badBcc}" is not a valid email` });

    setSending(true);
    try {
      // Build any paperwork attachments the agent has selected.
      let attachments = [];
      if (attachSelected.size > 0) {
        const built = await buildAttachments([...attachSelected], policy.planNumber, isCancelled);
        attachments = built.attachments;
      }

      const authFetchResult = await authedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/emails/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: policy.email,
          cc: ccList.length ? ccList : undefined,
          bcc: bccList.length ? bccList : undefined,
          subject: finalSubject,
          html: finalHtml,
          text: finalText,
          tag: active,
          attachments: attachments.length ? attachments.map(({ size, ...a }) => a) : undefined,
        }),
      }, toast);
      if (!authFetchResult) return; // 401 already handled
      const { res, json: j } = authFetchResult;
      if (res.ok && j?.status === 'sent') {
        toast({ title: 'Email sent', description: `Delivered to ${policy.email}${attachments.length ? ` with ${attachments.length} attachment${attachments.length === 1 ? '' : 's'}` : ''}.` });
      } else if (res.ok && j?.status === 'queued') {
        toast({ title: 'Queued (Resend not configured)', description: 'Add RESEND_API_KEY + RESEND_SENDER to backend/.env, then click Send again.' });
      } else {
        toast({ title: 'Send failed', description: j?.detail || 'Please retry.' });
      }
    } catch (e) {
      toast({ title: 'Send failed', description: String(e?.message || e) });
    } finally {
      setSending(false);
    }
  };

  const doCopy = async (kind) => {
    try {
      if (kind === 'subject') await navigator.clipboard.writeText(finalSubject);
      else if (kind === 'text') await navigator.clipboard.writeText(finalText);
      else if (kind === 'html') await copyRichHtml(finalHtml, finalText);
      else if (kind === 'designed') await copyRichHtml(finalHtml, finalText);
      flash(kind);
      toast({
        title: kind === 'designed' ? 'Designed email copied' : (kind[0].toUpperCase() + kind.slice(1) + ' copied'),
        description: kind === 'designed' ? 'Paste into Outlook or Gmail — the design + images will come across.' : 'Paste into your email client.'
      });
    } catch {
      toast({ title: 'Copy failed', description: 'Long-press the text area and copy manually.' });
    }
  };

  return (
    <section data-testid="email-templates" className="bg-white rounded-2xl border border-[#1F0068]/5 p-5 md:p-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF5C39] font-bold">Email templates</div>
          <h3 className="text-xl font-bold text-[#1F0068] mt-1">One-click designed customer emails</h3>
          <p className="text-xs text-[#1F0068]/70 mt-1">Auto-filled with this customer’s details. Copy the designed HTML (with images) or plain text and paste into Outlook, Gmail or any email tool.</p>
        </div>
        <a href={mailto} data-testid="email-open-mailto">
          <Button variant="outline" className="rounded-full border-[#1F0068] text-[#1F0068] hover:bg-[#1F0068] hover:text-white gap-1.5"><Mail className="w-4 h-4" /> Open in email app</Button>
        </a>
        <Button
          data-testid="email-send-now"
          onClick={() => {
            if (!policy.email) { toast({ title: 'No customer email on file' }); return; }
            setComposerOpen(true);
          }}
          className={`rounded-full gap-1.5 ${resendEnabled ? 'bg-[#00b67a] hover:bg-[#00a068] text-white' : 'bg-[#1F0068]/40 hover:bg-[#1F0068]/50 text-white'}`}
        >
          {resendEnabled ? <Zap className="w-4 h-4" /> : <ZapOff className="w-4 h-4" />}
          Preview &amp; edit email
        </Button>
      </div>

      <div data-testid="resend-status-inline" className="mt-2 text-[11px] text-[#1F0068]/60">
        {resendEnabled ? 'Resend is live — Send delivers straight to the customer’s inbox.' : 'Resend not configured yet — clicking Send will queue the email until an API key is added.'}
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {emailTabs.map((t) => (
          <button
            key={t.id}
            data-testid={`email-tab-${t.id}`}
            onClick={() => setActive(t.id)}
            className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${active === t.id ? 'bg-[#1F0068] text-white' : 'bg-[#FFF6EB] text-[#1F0068] hover:bg-[#FFEDD6]'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ---------- Recipients + attachments ---------- */}
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <div className="bg-[#FFF6EB] rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Recipients</div>
            {!showCcBcc && <button type="button" onClick={() => setShowCcBcc(true)} data-testid="email-add-cc-bcc" className="text-[11px] text-[#FF5C39] font-semibold underline">+ Add CC / BCC</button>}
          </div>
          <div>
            <Label className="text-[11px] text-[#1F0068]/70 font-semibold">To</Label>
            <Input readOnly value={policy.email || '(no email on file)'} className="h-10 mt-1 rounded-xl bg-white border-[#1F0068]/10 text-sm" />
          </div>
          {showCcBcc && (
            <>
              <div className="mt-2">
                <Label className="text-[11px] text-[#1F0068]/70 font-semibold">CC</Label>
                <Input data-testid="email-cc" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="partner@example.com, landlord@example.com" className="h-10 mt-1 rounded-xl bg-white border-[#1F0068]/10 text-sm" />
              </div>
              <div className="mt-2">
                <Label className="text-[11px] text-[#1F0068]/70 font-semibold">BCC</Label>
                <Input data-testid="email-bcc" value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="records@yourcompany.com" className="h-10 mt-1 rounded-xl bg-white border-[#1F0068]/10 text-sm" />
              </div>
            </>
          )}
        </div>

        <div className="bg-[#FFF6EB] rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attach paperwork <span className="opacity-70 normal-case tracking-normal">({attachSelected.size})</span></div>
            <div className="flex gap-2 text-[11px]">
              <button type="button" onClick={() => setAttachSelected(new Set(availDocs.map((d) => d.key)))} data-testid="email-attach-all" className="text-[#FF5C39] font-semibold underline">All</button>
              <button type="button" onClick={() => setAttachSelected(new Set())} data-testid="email-attach-none" className="text-[#1F0068]/70 font-semibold underline">None</button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-1">
            {availDocs.map((doc) => {
              const on = attachSelected.has(doc.key);
              return (
                <label key={doc.key} data-testid={`email-attach-${doc.key}`} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer transition-colors ${on ? 'bg-white border-[#FF5C39]' : 'bg-white/50 border-transparent hover:border-[#1F0068]/20'}`}>
                  <input type="checkbox" checked={on} onChange={() => toggleAttach(doc.key)} className="w-3.5 h-3.5 accent-[#FF5C39]" />
                  <FileText className="w-3.5 h-3.5 text-[#1F0068]/50" />
                  <span className="text-xs font-semibold text-[#1F0068] flex-1">{doc.label}</span>
                  {on && <CheckCircle2 className="w-3.5 h-3.5 text-[#FF5C39]" />}
                </label>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-4 bg-[#FFF6EB] rounded-xl p-3">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Subject line <span className="opacity-70 normal-case tracking-normal">(editable)</span></div>
          <CopyBtn testId="email-copy-subject" onClick={() => doCopy('subject')} label="Copy subject" ok={copied === 'subject'} />
        </div>
        <Input
          data-testid="email-subject-editable"
          value={finalSubject}
          onChange={(e) => setSubjectOverride(e.target.value)}
          className="h-11 rounded-xl bg-white border-[#1F0068]/10 font-semibold text-[#1F0068] text-sm"
        />
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center bg-[#FFF6EB] rounded-full p-1 gap-1">
            <button data-testid="email-mode-preview" onClick={() => setMode('preview')} className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full ${mode === 'preview' ? 'bg-white text-[#1F0068] shadow-sm' : 'text-[#1F0068]/70'}`}><Eye className="w-3 h-3" /> Preview</button>
            <button data-testid="email-mode-html" onClick={() => setMode('html')} className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full ${mode === 'html' ? 'bg-white text-[#1F0068] shadow-sm' : 'text-[#1F0068]/70'}`}><Code2 className="w-3 h-3" /> HTML</button>
            <button data-testid="email-mode-text" onClick={() => setMode('text')} className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full ${mode === 'text' ? 'bg-white text-[#1F0068] shadow-sm' : 'text-[#1F0068]/70'}`}><FileText className="w-3 h-3" /> Plain text</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <CopyBtn testId="email-copy-designed" onClick={() => doCopy('designed')} label="Copy designed email" tone="primary" ok={copied === 'designed'} />
            <CopyBtn testId="email-copy-html" onClick={() => doCopy('html')} label="Copy HTML" ok={copied === 'html'} />
            <CopyBtn testId="email-copy-text" onClick={() => doCopy('text')} label="Copy plain text" ok={copied === 'text'} />
          </div>
        </div>

        <div className="mt-3 rounded-xl bg-[#F5F1EA] p-3">
          {mode === 'preview' && (
            <iframe
              data-testid="email-preview-frame"
              title="Email preview"
              srcDoc={finalHtml}
              className="w-full h-[560px] rounded-lg bg-white border border-[#1F0068]/10"
              sandbox=""
            />
          )}
          {mode === 'html' && (
            <textarea
              data-testid="email-html-textarea"
              value={finalHtml}
              onChange={(e) => setHtmlOverride(e.target.value)}
              className="w-full h-[420px] rounded-lg bg-white border border-[#1F0068]/10 p-3 text-[11px] font-mono text-[#1F0068]/80 leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#FF5C39]/30"
            />
          )}
          {mode === 'text' && (
            <textarea
              data-testid="email-body-textarea"
              value={finalText}
              onChange={(e) => setTextOverride(e.target.value)}
              className="w-full h-[420px] rounded-lg bg-white border border-[#1F0068]/10 p-3 text-[12px] font-mono text-[#1F0068]/85 leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#FF5C39]/30"
            />
          )}
          <p className="mt-2 text-[10px] text-[#1F0068]/50 leading-relaxed">Tip: switch to HTML or Plain text tabs to edit the body directly. Any edits made here are applied before the email is sent.</p>
        </div>
      </div>

      <EmailComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        policy={policy}
        templateKind={active}
        initialSubject={finalSubject}
        initialHtml={finalHtml}
        initialText={finalText}
        defaultAttachmentKeys={[...attachSelected]}
        tag={active}
      />
    </section>
  );
};

export default EmailTemplates;
